import { defaultTheme } from '@hrworks/design-system/themes'

const {
  eclipseTier1: tier1,
  eclipseTier2Color: color,
  eclipseTier2Typography: typography,
} = defaultTheme

/**
 * Application theme configuration
 * Extends the SmartFace design system default theme (Eclipse) with convenient shortcuts.
 * Every shortcut resolves to an Eclipse token, so the values follow the active light/dark scheme.
 */
export const theme = {
  ...defaultTheme,
  // Add convenient shortcuts to commonly used theme properties
  colors: {
    background: {
      default: color.background.neutral.subtle.default,
      paper: color.background.neutral.subtlest.default,
      hover: color.background.brand.bold.hovered,
      disabled: color.background.disabled.default,
    },
    border: {
      default: color.border.input,
      light: color.border.subtle,
    },
    text: {
      primary: color.text.default,
      secondary: color.text.subtle,
      error: color.text.error.default,
      success: color.text.success.default,
      warning: color.text.warning.default,
      disabled: color.text.disabled,
    },
    primary: {
      main: color.background.brand.bold.default,
      light: color.background.brand.bold.hovered,
      dark: color.background.brand.bold.pressed,
      contrastText: color.text.inverse,
    },
    error: {
      main: color.background.error.bold.default,
      light: color.background.error.subtle,
      dark: color.text.error.onSubtle,
    },
    success: {
      main: color.background.success.bold.default,
      light: color.background.success.subtle,
      dark: color.text.success.onSubtle,
    },
    warning: {
      main: color.background.warning.bold.default,
      light: color.background.warning.subtle,
      dark: color.text.warning.onSubtle,
    },
  },
  spacing: {
    xs: tier1.spacing['4px'],
    sm: tier1.spacing['8px'],
    md: tier1.spacing['16px'],
    lg: tier1.spacing['24px'],
    xl: tier1.spacing['32px'],
  },
  typography: {
    body1: typography.bodyMd,
    fontSize: {
      sm: tier1.typography.fontSize['14'],
      md: tier1.typography.fontSize['16'],
    },
    fontWeightMedium: tier1.typography.fontWeight.medium,
  },
  borderRadius: {
    sm: tier1.radius['4px'],
    md: tier1.radius['8px'],
  },
  shadows: {
    sm: color.surface.elevation.shadow.raised.boxShadow,
  },
} as const

export type Theme = typeof theme
